/**
 * URL / 路由工具函数
 *
 * 提供安全的 URL 操作、外部链接处理、查询字符串解析等通用功能。
 *
 */

function isAbsoluteOrProtocolRelativeUrl(path: string): boolean {
  if (isExternalUrl(path)) return true;
  return /^[a-z][a-z0-9+.-]*:/i.test(path);
}

/**
 * 安全地在新标签页中打开 URL
 *
 * 自动添加 `noopener,noreferrer` 安全属性，防止新窗口通过 window.opener 访问原页面。
 * 出于安全考虑，拒绝 `javascript:` / `data:` / `vbscript:` / `file:` 协议。
 *
 * @param url - 要打开的 URL
 * @param features - 窗口特性字符串（可选）
 * @returns 新窗口的引用，打开失败返回 null
 *
 * @example
 * ```ts
 * openInNewTab('https://example.com');
 * openInNewTab('/detail/123'); // 相对路径也支持
 * ```
 */
export function openInNewTab(url: string, features?: string): Window | null {
  if (!url || typeof window === 'undefined') return null;

  const trimmedUrl = url.trim();
  if (/^(javascript|data|vbscript|file):/i.test(trimmedUrl)) {
    return null;
  }

  const finalFeatures = features
    ? `noopener,noreferrer,${features}`
    : 'noopener,noreferrer';
  return window.open(trimmedUrl, '_blank', finalFeatures);
}

/**
 * 标准化外部 URL
 *
 * 自动为缺少协议的 URL 补充 `https://` 前缀。
 *
 * @param url - 原始 URL
 * @returns 标准化后的 URL
 *
 * @example
 * ```ts
 * normalizeExternalUrl('example.com')           // 'https://example.com'
 * normalizeExternalUrl('http://example.com')     // 'http://example.com'
 * normalizeExternalUrl('https://example.com')    // 'https://example.com'
 * normalizeExternalUrl('//cdn.example.com/a.js') // '//cdn.example.com/a.js'
 * ```
 */
export function normalizeExternalUrl(url: string): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('//')
  ) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

/**
 * 判断是否为外部链接
 *
 * @param url - URL 字符串
 * @returns 是否为外部链接
 *
 * @example
 * ```ts
 * isExternalUrl('https://example.com')  // true
 * isExternalUrl('http://example.com')   // true
 * isExternalUrl('//cdn.example.com')    // true
 * isExternalUrl('/dashboard')           // false
 * isExternalUrl('about')               // false
 * ```
 */
export function isExternalUrl(url: string): boolean {
  if (!url) return false;
  return /^(https?:)?\/\//.test(url.trim());
}

/**
 * 拼接公共资源路径
 *
 * @param path - 资源相对路径
 * @param baseUrl - 基础 URL，默认为空（使用相对路径）
 * @returns 完整的资源 URL
 *
 * @example
 * ```ts
 * getPublicUrl('/images/logo.png', 'https://cdn.example.com')
 * // => 'https://cdn.example.com/images/logo.png'
 *
 * getPublicUrl('images/logo.png')
 * // => '/images/logo.png'
 * ```
 */
export function getPublicUrl(path: string, baseUrl?: string): string {
  if (!path) return '';

  // 已经是完整 URL
  if (isExternalUrl(path)) return path;

  // 拼接 baseUrl
  if (baseUrl) {
    const normalizedBase = baseUrl.endsWith('/')
      ? baseUrl.slice(0, -1)
      : baseUrl;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${normalizedBase}${normalizedPath}`;
  }

  // 确保以 / 开头
  return path.startsWith('/') ? path : `/${path}`;
}

/**
 * 构建查询字符串
 *
 * 自动过滤 null、undefined 和空字符串值。
 *
 * @param params - 参数对象
 * @returns 查询字符串（不含 '?'）
 *
 * @example
 * ```ts
 * buildQueryString({ page: 1, keyword: 'test', status: null })
 * // => 'page=1&keyword=test'
 *
 * buildQueryString({ tags: ['a', 'b'] })
 * // => 'tags=a&tags=b'
 * ```
 */
export function buildQueryString(
  params: Record<
    string,
    string | number | boolean | string[] | null | undefined
  >,
): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== null && item !== undefined && item !== '') {
          searchParams.append(key, String(item));
        }
      }
    } else {
      searchParams.append(key, String(value));
    }
  }

  return searchParams.toString();
}

/**
 * 解析查询字符串为对象
 *
 * @param search - 查询字符串（可以带或不带 '?'）
 * @returns 参数对象
 *
 * @example
 * ```ts
 * parseQueryString('?page=1&keyword=test')
 * // => { page: '1', keyword: 'test' }
 *
 * parseQueryString('tags=a&tags=b')
 * // => { tags: ['a', 'b'] }
 * ```
 */
export function parseQueryString(
  search: string,
): Record<string, string | string[]> {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  const result: Record<string, string | string[]> = {};

  for (const key of new Set(params.keys())) {
    const values = params.getAll(key);
    result[key] = values.length === 1 ? values[0] : values;
  }

  return result;
}

/**
 * 安全地打开外部链接（标准化 + 新标签页）
 *
 * @param url - 外部 URL
 * @returns 新窗口的引用
 *
 * @example
 * ```ts
 * openExternalUrl('example.com') // 自动补 https:// 并在新标签页打开
 * ```
 */
export function openExternalUrl(url: string): Window | null {
  return openInNewTab(normalizeExternalUrl(url));
}

/**
 * 获取资源 URL
 *
 * 处理资源路径，支持绝对 URL、data URI 和相对路径。
 * 绝对 URL 和 data URI 直接返回，其他路径通过 getPublicUrl 拼接。
 *
 * @param path - 资源路径
 * @param baseUrl - 基础 URL（可选），用于拼接相对路径
 * @returns 完整的资源 URL 或 undefined
 *
 * @example
 * ```ts
 * getResourceUrl('https://cdn.example.com/avatar.png')  // 直接返回
 * getResourceUrl('data:image/png;base64,...')            // 直接返回
 * getResourceUrl('/uploads/avatar.png', '/api')          // '/api/uploads/avatar.png'
 * getResourceUrl(null)                                   // undefined
 * ```
 */
export function getResourceUrl(
  path?: string | null,
  baseUrl?: string,
): string | undefined {
  if (!path || typeof path !== 'string') {
    return undefined;
  }
  const trimmedPath = path.trim();
  if (trimmedPath === '') return undefined;

  // 绝对 URL / data URI 直接返回
  if (isAbsoluteOrProtocolRelativeUrl(trimmedPath)) {
    return trimmedPath;
  }
  // 所有其他路径统一走 getPublicUrl 处理
  return getPublicUrl(trimmedPath, baseUrl);
}
